import { trainingCourseManager } from "@/services/training-course/training-course-manager";
import { triadManager } from "@/services/triad/triad-manager";
import { defineStore } from "pinia";
import { useMainStore } from "@/store/main/main";
import * as Sentry from "@sentry/vue";
import router from "@/router";
import { analyticsTagsConstantes } from "@/constantes/analyticsTags";

export const useTrainingCourseStore = defineStore({
  id: "TrainingCourse",
  state: () => {
    return {
      count: {},
      trainingCourseList: [],
      trainingCourse: {},
      infos: {},
      useSkillAdminConfig: false,
      pinnedTrainingCourseId: null,
    };
  },

  actions: {
    findTrainingCourse(list, id) {
      if (list && typeof list === "object") {
        return Object.values(list).find((item) => item.id === id);
      }
    },

    setUseSkillAdminConfig(value) {
      this.useSkillAdminConfig = value;
    },

    snapshotPinnedTrainingCourseId(ignore = false) {
      const stored = window.localStorage.getItem("studea-oldTrainingCourse");
      this.pinnedTrainingCourseId = !ignore && stored ? parseInt(stored) : null;
      return this.pinnedTrainingCourseId;
    },

    fetchTrainingCourseList(payload = {}, design) {
      const params = {
        archive: payload.archive ? payload.archive : 1,
        search: payload.search ? payload.search : "",
        page: payload.page ? payload.page : 0,
        id: this.snapshotPinnedTrainingCourseId(payload.archive === 2),
      };

      if (payload.titleRncp) {
        params["titleRncp"] = payload.titleRncp;
      }
      if (payload.degree) {
        params["degree"] = payload.degree;
      }
      if (payload.institution) {
        params["institution"] = payload.institution;
      }
      if (payload.component) {
        params["component"] = payload.component;
      }
      if (payload.trainingCourseName) {
        params["trainingCourseName"] = payload.trainingCourseName;
      }

      if (useMainStore().isInternet) {
        return trainingCourseManager.loadTrainingCourseList(params).then((res) => {
          if (res && res.trainingCourses) {
            this.trainingCourseList = res.trainingCourses;
            this.count = res.count;

            if (Object.keys(res.trainingCourses).length && !window.localStorage.getItem("studea-oldTrainingCourse")) {
              this.setTrainingCourse(res.trainingCourses[0]);
            } else {
              this.toggleTrainingCourse(
                parseInt(window.localStorage.getItem("studea-oldTrainingCourse")),
                design,
                !!payload.search,
              );
            }
          }

          if (
            !payload.search &&
            res.trainingCourses &&
            Object.keys(res.trainingCourses).length &&
            this.trainingCourse &&
            this.trainingCourse.nbTriad < 100
          ) {
            this.fetchTrainingCourseTriads(
              {
                id: this.trainingCourse.id,
                toggle: true,
                archive: payload.archive ? payload.archive : 1,
              },
              design,
            );
          }
        });
      }
    },

    fetchTrainingCourse(id) {
      return trainingCourseManager.loadTrainingCourse(id).then((trainingCourse) => {
        this.setTrainingCourse(trainingCourse);
        this.setUseSkillAdminConfig(trainingCourse.useSkillAdminConfig);
      });
    },

    fetchTrainingCourseTriads(payload, design, currentRouter = router) {
      if (payload.toggle) {
        this.toggleTrainingCourse(payload.id, design);
      }
      if (this.trainingCourse) {
        if (currentRouter.currentRoute.value.name !== "TrainingCourseList") {
          trainingCourseManager.setNewCfaLogo(this.trainingCourse);
        }
        this.trainingCourse["loading"] = true;
        return triadManager
          .loadTriadList(payload.id, payload.archive, payload.search)
          .then((list) => {
            this.setTrainingCourseTriads(list);
          })
          .finally(() => {
            if (this.trainingCourse) {
              this.trainingCourse["loading"] = false;
            }
          });
      }
    },

    setTrainingCourseList(trainingCourseList) {
      this.trainingCourseList = trainingCourseList;
    },

    setCount(count) {
      this.count = count;
    },

    setTrainingCourse(trainingCourse) {
      Sentry.setTag("trainingCourse", trainingCourse?.id);
      if (trainingCourse?.id) {
        window.localStorage.setItem("studea-oldTrainingCourse", trainingCourse.id);
      }
      this.trainingCourse = trainingCourse;
    },

    toggleTrainingCourse(id, design, search = false) {
      if (Object.keys(this.trainingCourseList).length) {
        this.trainingCourse = this.findTrainingCourse(this.trainingCourseList, id);
        if (!this.trainingCourse && !search) {
          window.localStorage.setItem("studea-oldTrainingCourse", id);
          this.fetchTrainingCourse(id);
        } else if (!search) {
          this.setTrainingCourse(this.trainingCourse);
        } else {
          this.setTrainingCourse(this.trainingCourseList[0]);
        }
      }
    },

    setTrainingCourseTriads(list) {
      if (this.trainingCourse && this.trainingCourse.id) {
        this.trainingCourse["crews"] = list;
      }
    },

    setGradeBookConfigDone(value) {
      if (this.trainingCourse?.id) {
        this.trainingCourse["gradeBookConfigDone"] = value;
      }
    },
    setTrainingCourseInfos(triad) {
      let infos = [];
      if (triad.trainingCourse && triad.trainingCourse.displayPresentation) {
        infos.push({
          label: triad.trainingCourse.blocNamePresentation,
          content: triad.trainingCourse.presentation,
          type: "presentation",
          tagId: analyticsTagsConstantes.TRAINING_COURSE_INFO_PRESENTATION,
        });
      }
      if (triad.trainingCourse && triad.trainingCourse.displayContact) {
        infos.push({
          label: triad.trainingCourse.blocNameContact,
          content: triad.trainingCourse.contact,
          type: "contact",
          tagId: analyticsTagsConstantes.TRAINING_COURSE_INFO_CONTACT,
        });
      }
      if (triad.trainingCourse && triad.trainingCourse.displayPartner) {
        infos.push({
          label: triad.trainingCourse.blocNamePartner,
          content: triad.trainingCourse.partner,
          type: "partner",
          tagId: analyticsTagsConstantes.TRAINING_COURSE_INFO_PARTNER,
        });
      }
      if (triad.trainingCourse && triad.trainingCourse.displayCommunication) {
        infos.push({
          label: triad.trainingCourse.blocNameCommunication,
          content: triad.trainingCourse.communication,
          type: "communication",
          tagId: analyticsTagsConstantes.TRAINING_COURSE_INFO_COMMUNICATION,
        });
      }

      this.infos = infos;
    },

    addTrainingCourses(trainingCourses) {
      const current = Array.isArray(this.trainingCourseList)
        ? this.trainingCourseList
        : Object.values(this.trainingCourseList || {});
      const incoming = Array.isArray(trainingCourses) ? trainingCourses : Object.values(trainingCourses || {});
      const knownIds = new Set(current.map((trainingCourse) => trainingCourse.id));
      this.trainingCourseList = current.concat(incoming.filter((trainingCourse) => !knownIds.has(trainingCourse.id)));
    },

    resetTrainingCourse() {
      this.trainingCourse = {};
    },

    setDocumentBlockName(blocNameDocument) {
      this.trainingCourse.blocNameDocument = blocNameDocument;
    },

    init() {
      this.count = {};
      this.trainingCourseList = [];
      this.trainingCourse = {};
      this.infos = {};
      this.useSkillAdminConfig = false;
      this.pinnedTrainingCourseId = null;
    },
  },
});
